// Per-document access (task 402.9, ADR-066 §3–4): assignments — who may read one restricted document in full,
// until when — and access requests, which a masked reader raises and an assigner decides.
//
// Types mirror the api's DocumentAssignmentDto / AccessRequestDto / AssignmentCandidateDto. They live here, not in
// the hand-written schema stub, like the Access admin's own module.

import { api } from '../client';
import type { PagedResponse } from '../generated/schema';
import type { MfaStep } from '../contracts/session';

const ASSIGNMENTS = 'api/console/assignments';
const REQUESTS = 'api/console/access-requests';
const DOCUMENTS = 'api/console/documents';

/** VIEW is an assignment; ACTION is a delegation (task 402.13, not yet issued by the console). */
export type AssignmentScope = 'VIEW' | 'ACTION';

/** One action the caller could hand to somebody else on this document, right now (task 402.13). */
export interface DelegatableAction {
  transitionId: string;
  transitionCode: string;
  transitionName: string;
  fromStateCode: string;
  fromStateName: string;
}

export interface DocumentAssignment {
  id: string;
  documentId: string;
  documentNumber: string;
  documentTypeCode: string;
  documentTypeName: string;
  documentStatus: string;
  assigneeUserId: string;
  assigneeName: string;
  assignedByUserId: string;
  assignedByName: string;
  scope: AssignmentScope | string;
  reason: string;
  validFrom: string;
  validUntil: string;
  revokedAt: string | null;
  revokeReason: string | null;
  isActive: boolean;
  // ---- Delegation (task 402.13); all null on a VIEW row --------------------------------------------------
  transitionId?: string | null;
  transitionCode?: string | null;
  transitionName?: string | null;
  fromStateCode?: string | null;
  consumedAt?: string | null;
  /** Active, unconsumed, and the document still in the state it was granted for. */
  isUsable?: boolean;
}

/** Someone the current document could be assigned to; `alreadyAssigned` is offered but not selectable. */
export interface AssignmentCandidate {
  userId: string;
  username: string;
  displayName: string;
  alreadyAssigned: boolean;
}

export type AccessRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';

export interface AccessRequest {
  id: string;
  documentId: string;
  documentNumber: string;
  documentTypeCode: string;
  documentTypeName: string;
  requesterUserId: string;
  requesterName: string;
  reason: string;
  status: AccessRequestStatus | string;
  decidedByUserId: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  assignmentId: string | null;
  createdDate: string;
  expiresAt: string;
  /**
   * Who the document is about — client, account, payer or beneficiary, account numbers — already masked by the api
   * whatever the caller's view (task 402.16). Empty when the type names nobody; absent from an api older than 402.16.
   */
  subject?: AccessRequestSubjectField[];
}

/** One masked subject line, e.g. `{ label: 'Beneficiary', value: 'John S.' }`. */
export interface AccessRequestSubjectField {
  label: string;
  value: string;
}

/** The approver inbox: whether the caller approves anything at all, and what is waiting (task 402.16). */
export interface PendingApprovals {
  canApprove: boolean;
  requests: AccessRequest[];
}

/**
 * Local key factory, so the shared queryKeys module stays untouched (the Access admin's `accessKeys` precedent).
 * `all` is the invalidation root: every write here invalidates it.
 */
export const assignmentKeys = {
  accessTrail: (documentId: string) => ['assignments', 'access-trail', documentId] as const,
  delegatable: (documentId: string) => ['assignments', 'delegatable', documentId] as const,
  delegations: (documentId: string) => ['assignments', 'delegations', documentId] as const,
  all: ['document-access'] as const,
  mine: (page: number, size: number) =>
    [...assignmentKeys.all, 'assignments', 'mine', page, size] as const,
  myCount: () => [...assignmentKeys.all, 'assignments', 'my-count'] as const,
  ofDocument: (documentId: string) =>
    [...assignmentKeys.all, 'assignments', 'document', documentId] as const,
  candidates: (documentId: string, search: string) =>
    [...assignmentKeys.all, 'assignments', 'candidates', documentId, search] as const,
  myRequests: () => [...assignmentKeys.all, 'requests', 'mine'] as const,
  pendingRequests: () => [...assignmentKeys.all, 'requests', 'pending'] as const,
  pendingApprovals: () => [...assignmentKeys.all, 'requests', 'pending-approvals'] as const,
  requestsOfDocument: (documentId: string) =>
    [...assignmentKeys.all, 'requests', 'document', documentId] as const,
};

// ── Assignments ─────────────────────────────────────────────────────────────────────────────────

export function getMyAssignments(
  pageNumber = 1,
  pageSize = 25,
): Promise<PagedResponse<DocumentAssignment>> {
  return api.get<PagedResponse<DocumentAssignment>>(`${ASSIGNMENTS}/my`, {
    searchParams: { pageNumber, pageSize },
  });
}

/** Badge count: the caller's active assignments. */
export function getMyAssignmentCount(): Promise<number> {
  return api.get<number>(`${ASSIGNMENTS}/my/count`);
}

/** A document's assignments, active first then the last 90 days. Assigners and full-view holders only. */
export function getDocumentAssignments(documentId: string): Promise<DocumentAssignment[]> {
  return api.get<DocumentAssignment[]>(`${ASSIGNMENTS}/documents/${documentId}`);
}

/** Who this document may be assigned to: tenant members who can already view the type (task 402.9 api). */
export function getAssignmentCandidates(
  documentId: string,
  search?: string,
): Promise<AssignmentCandidate[]> {
  return api.get<AssignmentCandidate[]>(`${ASSIGNMENTS}/documents/${documentId}/candidates`, {
    searchParams: search ? { search } : {},
  });
}

export function assignDocument(
  documentId: string,
  body: { assigneeUserId: string; reason: string; hours?: number },
): Promise<DocumentAssignment> {
  return api.post<DocumentAssignment>(`${ASSIGNMENTS}/documents/${documentId}`, { json: body });
}

export function revokeAssignment(
  assignmentId: string,
  reason: string,
): Promise<DocumentAssignment> {
  return api.post<DocumentAssignment>(`${ASSIGNMENTS}/${assignmentId}/revoke`, {
    json: { reason },
  });
}

export function extendAssignment(assignmentId: string, hours: number): Promise<DocumentAssignment> {
  return api.post<DocumentAssignment>(`${ASSIGNMENTS}/${assignmentId}/extend`, { json: { hours } });
}

// ── Access requests ─────────────────────────────────────────────────────────────────────────────

export function getMyAccessRequests(take = 50): Promise<AccessRequest[]> {
  return api.get<AccessRequest[]>(`${REQUESTS}/my`, { searchParams: { take } });
}

/** The assigner inbox: pending requests on the types the caller may assign. */
export function getPendingAccessRequests(): Promise<AccessRequest[]> {
  return api.get<AccessRequest[]>(`${REQUESTS}/pending`);
}

/** The dashboard widget: pending requests plus whether the caller approves any type (task 402.16). */
export function getPendingApprovals(): Promise<PendingApprovals> {
  return api.get<PendingApprovals>(`${REQUESTS}/pending-approvals`);
}

export function getDocumentAccessRequests(documentId: string): Promise<AccessRequest[]> {
  return api.get<AccessRequest[]>(`${REQUESTS}/documents/${documentId}`);
}

/** Asks the type's assigners for the full view of one document the caller sees masked. */
export function requestAccess(documentId: string, reason: string): Promise<AccessRequest> {
  return api.post<AccessRequest>(`${REQUESTS}/documents/${documentId}`, { json: { reason } });
}

/** Approving creates the assignment with the request's reason (default 48 h). */
export function approveAccessRequest(
  requestId: string,
  body: { hours?: number; note?: string } = {},
): Promise<AccessRequest> {
  return api.post<AccessRequest>(`${REQUESTS}/${requestId}/approve`, { json: body });
}

export function rejectAccessRequest(requestId: string, note: string): Promise<AccessRequest> {
  return api.post<AccessRequest>(`${REQUESTS}/${requestId}/reject`, { json: { note } });
}

/**
 * One SECRET field, shown once (task 402.11). Exactly one of `value` and `mfa` is set: a tenant may ask for a
 * recent second factor first, and then the caller answers the challenge and asks again.
 */
export interface RevealedField {
  fieldPath: string;
  value: string | null;
  revealedAt: string | null;
  mfa?: MfaStep | null;
}

/**
 * Asks for one SECRET field of one document. A POST because the field path names what is being looked at, and a
 * GET would leave it in access logs and browser history; it writes nothing but its audit row.
 */
export function revealField(documentId: string, fieldPath: string): Promise<RevealedField> {
  return api.post<RevealedField>(`${DOCUMENTS}/${documentId}/reveal`, { json: { fieldPath } });
}

// ---- Delegation (task 402.13) --------------------------------------------------------------------------------

/** What the caller may hand to somebody else on this document right now — only what they hold themselves. */
export function getDelegatableActions(documentId: string): Promise<DelegatableAction[]> {
  return api.get<DelegatableAction[]>(`${ASSIGNMENTS}/documents/${documentId}/delegatable`);
}

/** Open delegations on a document, then the last 90 days of closed ones. */
export function getDocumentDelegations(documentId: string): Promise<DocumentAssignment[]> {
  return api.get<DocumentAssignment[]>(`${ASSIGNMENTS}/documents/${documentId}/delegations`);
}

/** Hands one person the full view plus one transition of the document's current state, once. */
export function delegateAction(
  documentId: string,
  body: { transitionCode: string; delegateUserId: string; reason: string; hours?: number },
): Promise<DocumentAssignment> {
  return api.post<DocumentAssignment>(`${ASSIGNMENTS}/documents/${documentId}/delegate`, {
    json: body,
  });
}

// ---- The access trail (task 402.15, owner decision T3) --------------------------------------------------------

/** One thing that happened to a document's access, as a reviewer reads it. */
export interface AccessTrailRow {
  id: string;
  userId: string;
  userName: string;
  /** DOCUMENT_VIEW_FULL | DOCUMENT_VIEW_DENIED | DOCUMENT_FIELD_REVEALED. */
  event: string;
  /** detail, list, attachment, kycDocument, export, edit or reveal. */
  operation: string;
  /** Only on a reveal: which field was shown. A path, never a value. */
  fieldPath: string | null;
  /** Only on a full view: ROLE or ASSIGNMENT — why they were allowed. */
  source: string | null;
  /** Views inside one five-minute window collapse; this is how many. */
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

/**
 * Who opened this document in full, who was refused, and who revealed which withheld field. Gated on the api by
 * ACCESS_AUDIT_VIEW, so a caller without it gets a refusal and the panel removes itself.
 */
export function getAccessTrail(documentId: string, take = 200): Promise<AccessTrailRow[]> {
  return api.get<AccessTrailRow[]>(
    `${ASSIGNMENTS}/documents/${documentId}/access-trail?take=${take}`,
  );
}
